import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home, ChevronLeft, BookOpen, ShoppingBag, Package,
  MapPin, Zap, Hammer, Users, MessageSquare, ChevronRight,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const STEPS = [
  {
    num: '01',
    title: 'Únete a una Guild',
    desc: 'El Housing es exclusivo de guilds. Necesitas pertenecer a una para acceder a tu propia casa.',
    highlight: null,
  },
  {
    num: '02',
    title: 'Compra la Casa',
    desc: 'Habla con el NPC de Guild House en las ciudades principales para adquirir el hogar de tu guild. Solo el Maestro de Guild puede realizar la compra inicial.',
    highlight: null,
  },
  {
    num: '03',
    title: 'Teleporta a tu Hogar',
    desc: 'Usa el NPC o escribe .gh tele en el chat para teletransportarte a tu casa en cualquier momento desde cualquier parte del mundo.',
    highlight: null,
  },
  {
    num: '04',
    title: 'Personaliza con el Mayordomo',
    desc: 'Habla con el Asistente de Guild House dentro de tu hogar. Cualquier miembro con el rango adecuado puede añadir mejoras y servicios a cambio de ',
    highlight: 'oro',
  },
];

const PURCHASES = [
  {
    icon: BookOpen,
    title: 'Entrenadores de Clase',
    color: 'rgba(0,229,255,0.12)',
    border: 'rgba(0,229,255,0.25)',
    items: [
      { name: 'Caballero de la Muerte' }, { name: 'Druida' }, { name: 'Cazador' },
      { name: 'Mago' }, { name: 'Paladín' }, { name: 'Sacerdote' },
      { name: 'Pícaro' }, { name: 'Chamán' }, { name: 'Brujo' }, { name: 'Guerrero' },
    ],
  },
  {
    icon: Zap,
    title: 'Entrenadores de Profesión',
    color: 'rgba(167,139,250,0.10)',
    border: 'rgba(167,139,250,0.28)',
    items: [
      { name: 'Alquimia' }, { name: 'Herrería' }, { name: 'Ingeniería' },
      { name: 'Sastrería' }, { name: 'Peletería' }, { name: 'Encantamiento' },
      { name: 'Joyería' }, { name: 'Inscripción' }, { name: 'Minería' },
      { name: 'Herboristería' }, { name: 'Desuello' },
      { name: 'Primeros Auxilios' }, { name: 'Pesca' }, { name: 'Cocina' },
    ],
  },
  {
    icon: ShoppingBag,
    title: 'Vendedores',
    color: 'rgba(52,211,153,0.10)',
    border: 'rgba(52,211,153,0.28)',
    items: [
      { name: 'Suministros Comerciales' },
      { name: 'Tabardos' },
      { name: 'Comida y Bebida' },
      { name: 'Componentes' },
      { name: 'Munición y Reparación' },
      { name: 'Próximamente nuevos NPCs', soon: true },
    ],
  },
  {
    icon: Users,
    title: 'Servicios',
    color: 'rgba(251,146,60,0.10)',
    border: 'rgba(251,146,60,0.28)',
    items: [
      { name: 'Posada' },
      { name: 'Banquero' },
      { name: 'Buzón' },
      { name: 'Subastador' },
      { name: 'Maestro de establos' },
      { name: 'Sanador de Espíritu' },
    ],
  },
  {
    icon: Hammer,
    title: 'Objetos',
    color: 'rgba(239,68,68,0.10)',
    border: 'rgba(239,68,68,0.28)',
    items: [
      { name: 'Fragua' },
      { name: 'Yunque' },
      { name: 'Cámara de la Guild' },
      { name: 'Silla de Barbero' },
    ],
  },
  {
    icon: MapPin,
    title: 'Portales',
    color: 'rgba(0,229,255,0.08)',
    border: 'rgba(0,229,255,0.22)',
    items: [
      { name: 'Forjaz' },
      { name: 'Darnassus' },
      { name: 'El Exodar' },
      { name: 'Entrañas' },
      { name: 'Cima del Trueno' },
      { name: 'Ciudad de Lunargenta' },
      { name: 'Shattrath' },
      { name: 'Dalaran' },
    ],
  },
];

const COMMANDS = [
  { cmd: '.guildhouse tele', alias: '.gh tele',     desc: 'Teletransportarte a tu Guild House.' },
  { cmd: '.guildhouse butler', alias: '.gh butler', desc: 'Invocar al Mayordomo junto a ti.' },
];

function PurchaseCard({ item }) {
  const Icon = item.icon;
  const iconColor = item.border.replace('0.28', '0.9').replace('0.25', '0.9').replace('0.22', '0.9');
  return (
    <div className="housing-purchase-card" style={{ background: item.color, borderColor: item.border }}>
      <div className="housing-purchase-header">
        <div className="housing-purchase-icon" style={{ color: iconColor }}>
          <Icon size={18} />
        </div>
        <span className="housing-purchase-title">{item.title}</span>
      </div>
      <ul className="housing-purchase-list">
        {item.items.map((entry) => (
          <li key={entry.name} className={`housing-purchase-item${entry.soon ? ' housing-purchase-item--soon' : ''}`}>
            <ChevronRight size={10} className="housing-purchase-chevron" />
            {entry.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HousingPage() {
  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Housing de Warband | Frostbound WotLK"
        description="Crea y personaliza el hogar de tu Warband en Frostbound. Añade vendedores, servicios, portales y más a tu Guild House."
        canonical="/jugabilidad/housing"
      />
      <Navbar />

      <main className="housing-main">

        {/* HERO */}
        <section className="housing-hero">
          <div className="housing-hero-bg" style={{ backgroundImage: "url('/sistemas/housing.jpg')" }} />
          <div className="housing-hero-overlay" />
          <div className="housing-hero-content anim-fade-up">
            <Link to="/jugabilidad" className="housing-back-link">
              <ChevronLeft size={14} />
              JUGABILIDAD
            </Link>
            <div className="housing-hero-icon"><Home size={36} /></div>
            <h1 className="housing-hero-title">HOUSING DE WARBAND</h1>
            <p className="housing-hero-subtitle">
              Un hogar para tu Warband. Crea y personaliza tu{' '}
              <span className="cyan-highlight">base de operaciones</span> en Azeroth.
            </p>
          </div>
        </section>

        {/* INTRO */}
        <section className="housing-intro anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <p className="housing-intro-text">
            El sistema de <strong>Guild House</strong> otorga a cada guild su propio espacio privado en Azeroth.
            Un mundo en fase única donde tú y tu Warband podéis reuniros, entrenar y preparar vuestras aventuras
            lejos del ruido de las ciudades. Comprad mejoras, añadid servicios y convertid vuestra casa en un cuartel general.
          </p>
        </section>

        {/* CÓMO EMPEZAR */}
        <section className="housing-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
          <div className="housing-section-header">
            <h2 className="housing-section-title">Cómo empezar</h2>
          </div>
          <div className="housing-steps">
            {STEPS.map((step) => (
              <div key={step.num} className="housing-step">
                <div className="housing-step-num">{step.num}</div>
                <h3 className="housing-step-title">{step.title}</h3>
                <p className="housing-step-desc">
                  {step.desc}
                  {step.highlight && (
                    <span className="housing-gold">{step.highlight}</span>
                  )}
                  {step.highlight && '.'}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* QUÉ PUEDES AÑADIR */}
        <section className="housing-section anim-fade-up" style={{ animationDelay: '0.2s' }}>
          <div className="housing-section-header">
            <h2 className="housing-section-title">Qué puedes añadir</h2>
            <p className="housing-section-sub">
              Habla con el Asistente de Guild House dentro de tu hogar para adquirir cualquiera de estas mejoras.
              Cada una se coloca predefinidamente en tu base de operaciones.
            </p>
          </div>
          <div className="housing-purchases-grid">
            {PURCHASES.map((item) => (
              <PurchaseCard key={item.title} item={item} />
            ))}
          </div>
        </section>

        {/* COMANDOS */}
        <section className="housing-section housing-section--narrow anim-fade-up" style={{ animationDelay: '0.25s' }}>
          <div className="housing-section-header">
            <h2 className="housing-section-title">Comandos de chat</h2>
          </div>
          <div className="housing-commands">
            {COMMANDS.map((c) => (
              <div key={c.cmd} className="housing-command-row">
                <div className="housing-command-codes">
                  <code className="housing-cmd">{c.cmd}</code>
                  <span className="housing-cmd-sep">·</span>
                  <code className="housing-cmd housing-cmd--alias">{c.alias}</code>
                </div>
                <span className="housing-cmd-desc">{c.desc}</span>
              </div>
            ))}
          </div>
          <p className="housing-commands-note">
            <MessageSquare size={13} style={{ marginRight: 5, flexShrink: 0 }} />
            Los comandos funcionan desde cualquier zona del mundo. No es necesario estar cerca de ningún NPC.
          </p>
        </section>

        {/* CTA */}
        <div className="housing-cta anim-fade-up" style={{ animationDelay: '0.3s' }}>
          <p className="housing-cta-text">DESCUBRE TODOS LOS SISTEMAS DE FROSTBOUND.</p>
          <Link to="/jugabilidad" className="cta-button" style={{ gap: 8 }}>
            JUGABILIDAD <ArrowRight size={14} />
          </Link>
        </div>

      </main>

      <Footer />
    </PageWrapper>
  );
}
